import { useState, useEffect } from "react"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { db } from "../../services/firebase"
import "./SeccionConfiguracion.css"

// ─────────────────────────────────────────────────
// Cloudinary upload helper (reutiliza el mismo de SubirVoucher)
// ─────────────────────────────────────────────────
const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME
const UPLOAD_PRESET = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET

async function subirACloudinary(file, folder) {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("upload_preset", UPLOAD_PRESET)
  formData.append("folder", folder)

  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: "POST",
    body: formData,
  })

  if (!res.ok) throw new Error("Error subiendo imagen")
  const data = await res.json()
  return data.secure_url
}

// ─────────────────────────────────────────────────
// Componente de una imagen configurable
// ─────────────────────────────────────────────────
function ConfigImagen({ titulo, descripcion, campo, urlActual, onGuardado }) {
  const [subiendo, setSubiendo]   = useState(false)
  const [preview, setPreview]     = useState(urlActual || "")
  const [exito, setExito]         = useState(false)
  const [error, setError]         = useState("")
  const [urlManual, setUrlManual] = useState("")
  const [modo, setModo]           = useState("subir") // "subir" | "url"

  async function handleArchivo(e) {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith("image/")) return setError("Solo se aceptan imágenes.")
    if (file.size > 5 * 1024 * 1024) return setError("La imagen no puede superar 5MB.")

    setSubiendo(true); setError(""); setExito(false)
    try {
      const url = await subirACloudinary(file, `lowtravel/config`)
      setPreview(url)
      await guardarURL(url)
    } catch(err) {
      setError("Error al subir la imagen. Verificá la configuración de Cloudinary.")
      console.error(err)
    } finally { setSubiendo(false) }
  }

  async function handleURLManual() {
    if (!urlManual.trim()) return setError("Ingresá una URL válida.")
    setSubiendo(true); setError("")
    try {
      setPreview(urlManual.trim())
      await guardarURL(urlManual.trim())
      setUrlManual("")
    } catch(err) {
      setError("Error al guardar.")
    } finally { setSubiendo(false) }
  }

  async function guardarURL(url) {
    await setDoc(doc(db, "config", "landing"), { [campo]: url }, { merge: true })
    onGuardado(campo, url)
    setExito(true)
    setTimeout(() => setExito(false), 3000)
  }

  return (
    <div className="cfg-imagen">
      <div className="cfg-imagen__info">
        <div className="cfg-imagen__titulo">{titulo}</div>
        <div className="cfg-imagen__desc">{descripcion}</div>
      </div>

      <div className="cfg-imagen__preview-wrap">
        {preview ? (
          <img src={preview} alt={titulo} className="cfg-imagen__preview" />
        ) : (
          <div className="cfg-imagen__placeholder">Sin imagen</div>
        )}
      </div>

      <div className="cfg-imagen__controles">
        <div className="cfg-imagen__tabs">
          <button onClick={()=>setModo("subir")} className={"cfg-tab"+(modo==="subir"?" active":"")}>📁 Subir archivo</button>
          <button onClick={()=>setModo("url")}   className={"cfg-tab"+(modo==="url"?" active":"")}>🔗 Usar URL</button>
        </div>

        {modo === "subir" ? (
          <label className={"cfg-imagen__upload-btn"+(subiendo?" cargando":"")}>
            {subiendo ? "Subiendo..." : "Elegir imagen"}
            <input type="file" accept="image/*" onChange={handleArchivo} style={{ display:"none" }} disabled={subiendo} />
          </label>
        ) : (
          <div style={{ display:"flex", gap:"8px" }}>
            <input
              type="url"
              placeholder="https://..."
              value={urlManual}
              onChange={e=>setUrlManual(e.target.value)}
              style={{ flex:1, padding:"9px 12px", background:"#1a1a1a", border:"1px solid #3a3a3a", borderRadius:"8px", color:"#e5e7eb", fontFamily:"Inter, sans-serif", fontSize:"0.875rem", outline:"none" }}
            />
            <button onClick={handleURLManual} disabled={subiendo} style={{ padding:"9px 16px", background:"#3b82f6", border:"none", borderRadius:"8px", color:"white", fontFamily:"Inter, sans-serif", fontWeight:700, cursor:"pointer" }}>
              Guardar
            </button>
          </div>
        )}

        {exito && <div className="cfg-exito">✓ Guardado correctamente</div>}
        {error && <div className="cfg-error">{error}</div>}
        {preview && (
          <div style={{ fontSize:"0.75rem", color:"#4b5563", marginTop:"4px", wordBreak:"break-all" }}>
            URL actual: {preview.length > 60 ? preview.slice(0,60)+"..." : preview}
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────
// Seccion principal
// ─────────────────────────────────────────────────
export default function SeccionConfiguracion() {
  const [config, setConfig] = useState({})
  const [cargando, setCargando] = useState(true)

  useEffect(() => {
    async function cargar() {
      try {
        const snap = await getDoc(doc(db, "config", "landing"))
        if (snap.exists()) setConfig(snap.data())
      } catch(e) { console.error(e) }
      finally { setCargando(false) }
    }
    cargar()
  }, [])

  function handleGuardado(campo, url) {
    setConfig(p => ({ ...p, [campo]: url }))
  }

  if (cargando) return (
    <div style={{ textAlign:"center", padding:"40px", color:"#6b7280" }}>Cargando configuración...</div>
  )

  return (
    <div>
      <div className="admin__seccion-header">
        <div className="admin__seccion-titulo">Configuración</div>
        <div className="admin__seccion-sub">Personalización de imágenes y contenido de la web</div>
      </div>

      <div className="cfg-seccion">
        <div className="cfg-seccion__titulo">🖼️ Imágenes de la landing</div>
        <div className="cfg-seccion__desc">
          Los cambios se aplican en tiempo real. El banner se ve en el hero de la página de inicio.
          La foto de Sergio aparece si decidís agregar esa sección en el futuro.
        </div>

        <div className="cfg-imagenes-lista">
          <ConfigImagen
            titulo="Banner del hero"
            descripcion="Imagen de fondo de la sección principal. Recomendado: horizontal, mínimo 1600×900px."
            campo="banner_url"
            urlActual={config.banner_url}
            onGuardado={handleGuardado}
          />
          <ConfigImagen
            titulo="Foto de Sergio"
            descripcion="Foto de perfil para la sección de presentación. Recomendado: vertical o cuadrada, mínimo 600×600px."
            campo="sergio_foto_url"
            urlActual={config.sergio_foto_url}
            onGuardado={handleGuardado}
          />
          <ConfigImagen
            titulo="Logo"
            descripcion="Versión con fondo claro o transparente del logo de Low Travel."
            campo="logo_url"
            urlActual={config.logo_url}
            onGuardado={handleGuardado}
          />
        </div>
      </div>

      {/* Preview de URLs guardadas */}
      <div className="cfg-seccion" style={{ marginTop:"20px" }}>
        <div className="cfg-seccion__titulo">📋 Configuración guardada</div>
        <div style={{ display:"flex", flexDirection:"column", gap:"8px", marginTop:"12px" }}>
          {Object.keys(config).length === 0 ? (
            <div style={{ color:"#4b5563", fontSize:"0.875rem" }}>Nada configurado todavía.</div>
          ) : Object.entries(config).map(([k,v])=>(
            <div key={k} style={{ display:"flex", gap:"12px", background:"#1a1a1a", border:"1px solid #2a2a2a", borderRadius:"8px", padding:"10px 14px", alignItems:"center" }}>
              <code style={{ fontSize:"0.8rem", color:"#f59e0b", background:"#2a2a2a", padding:"2px 8px", borderRadius:"4px", flexShrink:0 }}>{k}</code>
              <span style={{ fontSize:"0.8rem", color:"#6b7280", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{v}</span>
              <button onClick={()=>navigator.clipboard.writeText(v)} style={{ background:"none", border:"1px solid #3a3a3a", borderRadius:"6px", padding:"4px 8px", color:"#6b7280", cursor:"pointer", fontFamily:"Inter, sans-serif", fontSize:"0.75rem" }}>
                Copiar
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}